import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import PersistentIframeController from 'components/persistent-iframe/persistent-iframe-controller';
import { setSlotCloseRequest } from '../../actions/iframe-actions';
import { findCustomFrontendSlot } from './customFrontendManifest';

interface Props {
    slot: string;
    routeParams?: Record<string, string | number>;
    onClose?: () => void;
    children?: React.ReactNode;
}

function resolveRoute(template: string, params?: Record<string, string | number>): string {
    return template.replace(/:(\w+)/g, (fullMatch: string, key: string) => {
        const value = params?.[key];
        if (value === undefined || value === '') {
            // eslint-disable-next-line no-console
            console.warn(`CustomFrontendEmbed: missing routeParam "${key}" for template "${template}"`);
            return fullMatch;
        }
        return String(value);
    });
}

const slotStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    flex: '1 1 auto',
    display: 'flex',
};

const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 9999,
};

const CustomFrontendEmbed = ({
    slot, routeParams, onClose, children,
}: Props) => {
    const entry = findCustomFrontendSlot(slot);
    const dispatch = useDispatch();

    // Clear after firing so the signal doesn't re-trigger on remount.
    const closeRequestedSlot = useSelector(
        (state: any) => (entry ? state.iframeReducer?.[entry.appId]?.closeRequestedSlot : null),
    );
    React.useEffect(() => {
        if (entry && onClose && closeRequestedSlot === slot) {
            onClose();
            dispatch(setSlotCloseRequest(entry.appId, null));
        }
    }, [entry, slot, closeRequestedSlot]);

    if (!entry) {
        return <>{children ?? null}</>;
    }

    const resolvedRoute = resolveRoute(entry.iframeRoute, routeParams);
    const isOverlay = entry.presentation === 'overlay';
    const node = (
        <div style={slotStyle}>
            <PersistentIframeController
                appName={entry.appId}
                route={resolvedRoute}
                iframeClassName={isOverlay ? 'cf-overlay-iframe' : undefined}
            />
        </div>
    );

    if (isOverlay) {
        return ReactDOM.createPortal(
            <div style={overlayStyle}>{node}</div>,
            document.body,
        );
    }
    return node;
};

export default CustomFrontendEmbed;
